import React, { useState, useRef } from 'react';
import { Box } from '@mui/material';

/**
 * CinemaTiltCard - High-Level 3D Gyroscopic Perspective Tilt & Dynamic Spotlight Card Wrapper
 */
export const CinemaTiltCard = ({ children, accentColor = '#07a4f5', isDarkMode = true, sx = {} }) => {
  const [coords, setCoords] = useState({ x: 0, y: 0, mouseX: 0, mouseY: 0, isHovered: false });
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Subtle, cinema-grade tilt angles (-3.5deg to +3.5deg)
    const rotateX = ((mouseY - centerY) / centerY) * -3.2;
    const rotateY = ((mouseX - centerX) / centerX) * 3.2;

    setCoords({ x: rotateX, y: rotateY, mouseX, mouseY, isHovered: true });
  };

  const handleMouseLeave = () => {
    setCoords((prev) => ({ ...prev, x: 0, y: 0, isHovered: false }));
  };

  return (
    <Box
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={{
        perspective: 1200,
        width: '100%',
        ...sx,
      }}
    >
      <Box
        sx={{
          transform: coords.isHovered
            ? `rotateX(${coords.x}deg) rotateY(${coords.y}deg) translateY(-4px)`
            : 'rotateX(0deg) rotateY(0deg) translateY(0px)',
          transition: coords.isHovered
            ? 'transform 0.08s ease-out'
            : 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
          transformStyle: 'preserve-3d',
          position: 'relative',
          borderRadius: '28px',
          overflow: 'hidden',
        }}
      >
        {/* Dynamic Studio Spotlight Beam tracking cursor */}
        {coords.isHovered && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
              zIndex: 1,
              background: `radial-gradient(700px circle at ${coords.mouseX}px ${coords.mouseY}px, ${accentColor}${isDarkMode ? '22' : '15'}, transparent 70%)`,
              transition: 'background 0.05s ease',
            }}
          />
        )}
        {children}
      </Box>
    </Box>
  );
};

export default CinemaTiltCard;
